import { useState, type ReactNode } from "react";

import { useModuleBEM, useUiStyles } from "@b2bportal/core-themes";
import {
  CoreUiComponents,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import * as RadixRadioGroup from "@radix-ui/react-radio-group";
import clsx from "clsx";
import defaultStyles from "./RadioGroup.module.scss";

type RadioGroupItemClassNames = Partial<{
  itemContainer: string;
}>;

type RadioLabelProps = {
  id: string;
  disabled?: boolean;
} & PlatformComponentProps;

export type RadioGroupItemProps = {
  key?: string;
  value: string;
  id?: string;
  label?: ReactNode;
  labelComponent?: React.FC<RadioLabelProps>;
  disabled?: boolean;
  classes?: {
    label?: string;
    item?: string;
    indicator?: string;
  };
} & PlatformComponentProps;

export type RadioGroupProps = {
  // `options` is optional to support using children instead of a prop-based approach.
  options?: RadioGroupItemProps[];
  selectedOption?: string;
  onSelectValue: (value: string) => void;
  classNames?: RadioGroupItemClassNames & {
    container?: string;
  };
} & RadixRadioGroup.RadioGroupProps &
  PlatformComponentProps;

const DefaultLabel = ({
  children,
  disabled = false,
  className,
}: RadioLabelProps) => {
  const styles = useUiStyles(CoreUiComponents.RadioItem, defaultStyles);
  const [, cn] = useModuleBEM(styles, CoreUiComponents.RadioItem);

  return (
    <div className={clsx(cn("label", { disabled }), className)}>{children}</div>
  );
};

export const RadioItem = ({
  value,
  id,
  className,
  label = null,
  labelComponent,
  children,
  ariaLabel,
  selected,
  disabled = false,
  classes,
}: RadioGroupItemProps & {
  id: string;
  ariaLabel?: string;
  selected: boolean;
}) => {
  const styles = useUiStyles(CoreUiComponents.RadioItem, defaultStyles);
  const [block, cn] = useModuleBEM(styles, CoreUiComponents.RadioItem);

  const containerClassNames = clsx(
    block,
    cn("container", { selected }),
    className
  );
  const Label = labelComponent ?? DefaultLabel;

  return (
    <label className={containerClassNames} htmlFor={id}>
      <RadixRadioGroup.Item
        className={clsx(cn("item", { disabled, selected }), classes?.item)}
        value={value}
        id={id}
        aria-label={ariaLabel}
        disabled={disabled}
        aria-disabled={disabled}
      >
        <RadixRadioGroup.Indicator
          className={clsx(cn("indicator"), classes?.indicator)}
        />
      </RadixRadioGroup.Item>
      {label == null && children == null && labelComponent == null ? null : (
        <Label id={id} disabled={disabled} className={classes?.label}>
          {label}
          {children}
        </Label>
      )}
    </label>
  );
};

export const RadioGroup = ({
  options,
  disabled,
  selectedOption,
  onSelectValue,
  className,
  classNames = {},
  defaultValue,
  children,
  "aria-label": ariaLabel,
}: RadioGroupProps) => {
  const styles = useUiStyles(CoreUiComponents.RadioGroup, defaultStyles);
  const [block] = useModuleBEM(styles, CoreUiComponents.RadioGroup);

  const containerClassNames = clsx(block, className, classNames.container);

  const [internalSelectedOption, setInternalSelectedOption] = useState(
    selectedOption ?? defaultValue
  );

  const optionsComponents = options?.map((option) => {
    const elementId = option.key != null ? option.key : option.value;
    const selected = internalSelectedOption === option.value;
    return (
      <RadioItem
        value={option.value}
        id={elementId}
        key={elementId}
        className={clsx(option.className, classNames?.itemContainer)}
        label={option.label}
        labelComponent={option.labelComponent}
        ariaLabel={ariaLabel}
        selected={selected}
        disabled={option.disabled}
      />
    );
  });

  return (
    <RadixRadioGroup.RadioGroup
      className={containerClassNames}
      value={selectedOption}
      onValueChange={(v) => {
        setInternalSelectedOption(v);
        onSelectValue(v);
      }}
      defaultValue={defaultValue}
      data-selected={selectedOption}
      disabled={disabled}
    >
      {optionsComponents}
      {children}
    </RadixRadioGroup.RadioGroup>
  );
};

// Export Radix components so they can be used directly
RadioGroup.Root = RadixRadioGroup.RadioGroup;
RadioGroup.Item = RadioItem;
